import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Network, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import Card from "../Card";
import { useFusionSummary } from "./useFusionSummary";
import FusionScopeView from "./FusionScopeView";
import FusionChainView from "./FusionChainView";
import { DemonstrationBanner, PartialViewBanner, HelpDot } from "./FusionBits";
import FusionTrackedList from "./FusionTrackedList";
import FusionEmptyState from "./FusionEmptyState";
import FusionCoverageView from "./FusionCoverageView";
import { SCOPES, NON_ADDITIVE_TEXT, COVERAGE_PAGE_TITLE, formatMoney, formatTime, laneLabel } from "./fusionApi";

// The Dashboard card's open / closed state, remembered per viewer in this browser only.
const COLLAPSED_KEY = "axaris-fusion-card-collapsed";
const readCollapsed = () => {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
};
const writeCollapsed = (value) => {
  try { localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0"); } catch { /* storage blocked: the toggle still works for this visit */ }
};

const Kpi = ({ label, value, sub, accent, onClick, help }) => (
  <div
    onClick={onClick}
    className={`rounded-lg border px-3 py-2 ${accent ? "border-indigo-200 bg-indigo-50 cursor-pointer" : "border-border bg-theme-secondary"}`}
  >
    <div className={`text-xs flex items-center gap-1 ${accent ? "text-indigo-700" : "text-content-secondary"}`}>{label}{help && <HelpDot text={help} />}</div>
    <div className={`text-xl font-semibold mt-0.5 ${accent ? "text-indigo-700" : "text-content-primary"}`}>{value}</div>
    {sub && <div className={`text-xs ${accent ? "text-indigo-600" : "text-content-muted"}`}>{sub}</div>}
  </div>
);

/**
 * Universal Fusion: one component, two entry points (FUSION.md §1.7). Embedded under the Dashboard's
 * first four charts (driven by heatmap clicks) and full page at /Fusion. Views: dash → scope list →
 * chain. Every view repeats the per-user Partial view banner and the calculation time.
 *
 * Props:
 *  - embedded: renders as a Dashboard card.
 *  - summaryState: optional { summary, loading, error, reload } from the Dashboard, so the heatmap
 *    and the panel read the same summary.
 *  - heatmapTarget: { bucket, severity, nonce } — set by a heatmap cell click.
 *  - initialConsequence: a validated /Fusion?consequence= key that seeds the list's consequence filter.
 *  - initialView: "coverage" opens the Data coverage view (/Fusion?view=coverage); anything else is ignored.
 */
const FusionPanel = ({ embedded = false, summaryState, heatmapTarget, initialConsequence, initialView }) => {
  const own = useFusionSummary(!summaryState);
  const { summary, loading, error, reload } = summaryState || own;

  const [view, setView] = useState(initialView === "coverage" ? "coverage" : (embedded ? "dash" : "scope"));
  const [scope, setScope] = useState("Organization");
  const [bucket, setBucket] = useState(null);
  const [bucketSeverity, setBucketSeverity] = useState(null);
  const [anchor, setAnchor] = useState(null); // { type, id, focus? }
  // Chains the user moved through by centring on a point, oldest first: "← Back to …" pops the last.
  const [trail, setTrail] = useState([]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  // Only the Dashboard card collapses; the /Fusion page is always open.
  const [collapsed, setCollapsed] = useState(() => embedded && readCollapsed());
  const rootRef = useRef(null);

  const toggleCollapsed = () => setCollapsed((c) => { writeCollapsed(!c); return !c; });

  // The header's Data coverage button: a collapsed Dashboard card opens first, or the click would show nothing.
  const openCoverage = () => {
    if (embedded && collapsed) { setCollapsed(false); writeCollapsed(false); }
    setView("coverage");
  };

  // A heatmap click opens the Organization list filtered to that cell (FUSION.md §7.1).
  useEffect(() => {
    if (!heatmapTarget) return;
    // A heatmap click always shows its chains, so a collapsed card opens.
    setCollapsed(false);
    writeCollapsed(false);
    setScope("Organization");
    setBucket(heatmapTarget.bucket);
    setBucketSeverity(heatmapTarget.severity);
    setView("scope");
    rootRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [heatmapTarget]);

  // opts.from: the chain the user centred away from (pushed on the trail); opts.focus: a list path
  // segment ({ distance, lane }) to highlight. Opening from anywhere else starts a fresh trail.
  const openChain = useCallback((type, id, opts = {}) => {
    setTrail((t) => (opts.from ? [...t, opts.from] : []));
    setAnchor({ type, id: String(id), focus: opts.focus || null });
    setView("chain");
    rootRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  const backToPrevious = useCallback(() => {
    if (trail.length === 0) return;
    const prev = trail[trail.length - 1];
    setTrail(trail.slice(0, -1));
    setAnchor({ type: prev.type, id: String(prev.id), focus: null });
  }, [trail]);

  // Refresh and post-action re-reads go through refresh=true: the summary here, then the open
  // scope or chain view on the nonce bump (docs/architecture/FUSION.md "Caching"). The server
  // reuses a workspace built in the last few seconds, so each server rebuilds at most once.
  const refreshAll = useCallback(async () => {
    await reload({ refresh: true });
    setRefreshNonce((n) => n + 1);
  }, [reload]);

  const openScope = (key) => { setScope(key); setBucket(null); setBucketSeverity(null); setView("scope"); };

  const scopeLabel = SCOPES.find((s) => s.key === scope)?.label || scope;
  const scopeCount = (key) => summary?.scopes?.find((s) => s.scope === key)?.chains ?? 0;

  const breadcrumb = (
    <div className="flex items-center gap-2 flex-wrap mb-3 text-sm">
      <button className={view === "dash" ? "font-semibold text-content-primary" : "text-indigo-600 hover:underline"} onClick={() => setView("dash")}>Fusion</button>
      {(view === "scope" || view === "chain") && (<><span className="text-content-muted">/</span>
        <button className={view === "scope" ? "font-semibold text-content-primary" : "text-indigo-600 hover:underline"} onClick={() => setView("scope")}>{scopeLabel} chains</button></>)}
      {view === "chain" && anchor && trail.map((c, k) => (
        <React.Fragment key={`${c.type}:${c.id}:${k}`}>
          <span className="text-content-muted">/</span>
          <button className="text-indigo-600 hover:underline" onClick={() => {
            setTrail(trail.slice(0, k));
            setAnchor({ type: c.type, id: String(c.id), focus: null });
          }}>{c.label}</button>
        </React.Fragment>
      ))}
      {view === "chain" && anchor && (<><span className="text-content-muted">/</span><span className="font-semibold text-content-primary">{trail.length ? "Centred view" : "Chain"}</span></>)}
      {view === "coverage" && (<><span className="text-content-muted">/</span><span className="font-semibold text-content-primary">{COVERAGE_PAGE_TITLE}</span></>)}
    </div>
  );

  const body = (() => {
    // Data coverage opens without waiting for the summary (and despite a summary error): its own API
    // answers 403 to callers who may not see it, and the view shows that text. The same key below keeps
    // the view mounted (no second fetch) when the summary arrives.
    if (view === "coverage" && !summary) return (
      <>
        {breadcrumb}
        <FusionCoverageView key="coverage" refreshNonce={refreshNonce} />
      </>
    );
    if (loading && !summary) return (
      <div className="py-8 flex items-center justify-center gap-2 text-sm text-content-muted" role="status">
        <Loader2 className="h-4 w-4 animate-spin" /> Calculating connected chains…
      </div>
    );
    if (error === "forbidden") return <div className="py-8 text-center text-sm text-content-muted">You do not have access to Fusion for this organization.</div>;
    if (error) return <div className="py-8 text-center text-sm text-content-muted">Fusion could not be loaded. <button className="text-indigo-600 hover:underline" onClick={() => reload()}>Try again</button></div>;
    if (!summary) return null;

    return (
      <>
        <DemonstrationBanner show={summary.demonstrationData} />
        <PartialViewBanner show={summary.partialView} />

        {breadcrumb}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
          <Kpi label="Connected chains" value={summary.chains} sub="deduplicated" />
          <Kpi label="Risks in chains" value={summary.risksInChains} sub={`${summary.criticalRisksInChains} critical`} />
          <Kpi label="Confirmed links" value={`${summary.confirmedLinkSharePct}%`} sub="the rest are correlated" />
          <Kpi
            accent
            label="Correlated exposure"
            help={`${NON_ADDITIVE_TEXT} This figure is calculated once over the distinct risks of all your chains.`}
            value={formatMoney(summary.connectedExposure?.mean)}
            sub={`${summary.connectedExposure?.unpricedRisks ? `${summary.connectedExposure.unpricedRisks} risks not yet priced · ` : ""}open →`}
            onClick={() => openScope("Organization")}
          />
          <Kpi label="Modules in Fusion" value={summary.availableLanes?.length ?? 0} sub={(summary.availableLanes || []).map(laneLabel).join(", ") || "none connected"} />
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => openScope(s.key)}
              className={`text-xs px-2.5 py-1 rounded-lg border ${view !== "dash" && scope === s.key && !bucket ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-theme-secondary text-content-secondary border-border hover:border-indigo-200"} ${s.spine ? "ring-1 ring-indigo-200" : ""}`}
            >
              {s.label}<span className="ml-1 text-content-muted">{scopeCount(s.key)}</span>
            </button>
          ))}
          <span className="text-xs text-content-muted ml-1">Identity and Assets are the resolution spine.</span>
        </div>

        {view === "dash" && (
          <>
            {summary.chains === 0
              ? <FusionEmptyState variant="org" entityLinks={summary.entityLinks} coverage={summary.coverage}
                  coverageAvailable={summary.coverageAvailable === true} onOpenCoverage={openCoverage} />
              : <div className="text-sm text-content-secondary">{embedded ? "Pick a scope, or click a cell in the Risk Heatmap, to see connected chains." : "Pick a scope to see connected chains."}</div>}
            {/* Tracking uses the organization's own view, so it is shown only without Partial view. */}
            {!summary.partialView && <FusionTrackedList onOpen={openChain} refreshNonce={refreshNonce} />}
          </>
        )}
        {view === "scope" && (
          <FusionScopeView
            key={initialConsequence || ""}
            scope={scope}
            bucket={bucket}
            bucketSeverity={bucketSeverity}
            onClearBucket={() => { setBucket(null); setBucketSeverity(null); }}
            onOpen={openChain}
            refreshNonce={refreshNonce}
            orgChains={summary.chains}
            entityLinks={summary.entityLinks}
            coverage={summary.coverage}
            coverageAvailable={summary.coverageAvailable === true}
            onOpenCoverage={openCoverage}
            initialConsequence={initialConsequence}
          />
        )}
        {view === "coverage" && <FusionCoverageView key="coverage" refreshNonce={refreshNonce} />}
        {view === "chain" && anchor && (
          <FusionChainView
            anchor={anchor}
            onOpen={openChain}
            onBack={() => setView("scope")}
            previous={trail.length ? trail[trail.length - 1] : null}
            onBackTo={backToPrevious}
            onActionDone={refreshAll}
            refreshNonce={refreshNonce}
          />
        )}
      </>
    );
  })();

  const header = (
    <div className={`flex items-center gap-2 flex-wrap ${embedded && collapsed ? "" : "mb-3"}`}>
      {embedded ? (
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-controls="fusion-card-body"
          title={collapsed ? "Show Fusion" : "Hide Fusion"}
          className="inline-flex items-center gap-2 rounded-md -ml-1 px-1 hover:bg-theme-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4 text-content-secondary" />
            : <ChevronDown className="h-4 w-4 text-content-secondary" />}
          <Network className="h-5 w-5 text-indigo-600" />
          <span className="text-lg font-semibold text-content-primary">Universal Fusion</span>
        </button>
      ) : (
        <>
          <Network className="h-5 w-5 text-indigo-600" />
          <span className="text-lg font-semibold text-content-primary">Universal Fusion</span>
        </>
      )}
      {embedded && collapsed && summary && (
        <span className="text-xs text-content-secondary">
          {summary.chains} chain{summary.chains === 1 ? "" : "s"} · {formatMoney(summary.connectedExposure?.mean)}
        </span>
      )}
      {summary?.calculatedAt && <span className="text-xs text-content-muted">Calculated {formatTime(summary.calculatedAt)}</span>}
      {summary?.coverageAvailable && (
        <button type="button" onClick={openCoverage} className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-border text-content-secondary hover:text-indigo-700 hover:border-indigo-200">
          {COVERAGE_PAGE_TITLE}
        </button>
      )}
      <button onClick={refreshAll} className={`${summary?.coverageAvailable ? "" : "ml-auto "}inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-border text-content-secondary hover:text-indigo-700 hover:border-indigo-200`} disabled={loading}>
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
      </button>
    </div>
  );

  return embedded ? (
    <div ref={rootRef} id="fusion" className="mb-4 scroll-mt-20">
      <Card>{header}{!collapsed && <div id="fusion-card-body">{body}</div>}</Card>
    </div>
  ) : (
    <div ref={rootRef}>{header}{body}</div>
  );
};

export default FusionPanel;
